import { motion } from "framer-motion";
import { useTheme } from "@/contexts/ThemeContext";

export const ClientLogos = () => {
  const { colors } = useTheme();

  // Updated with the 10 logos from the uploaded image
  const companies = [
    {
      name: "Logo",
      logo: "/images/001.jpg",
      fallbackSize: "160x160",
    },
    {
      name: "Logo",
      logo: "/images/002.jpg",
      fallbackSize: "160x160",
    },
    {
      name: "Logo",
      logo: "/images/003.jpg",
      fallbackSize: "160x160",
    },
    {
      name: "Logo",
      logo: "/images/004.jpg",
      fallbackSize: "160x160",
    },
    {
      name: "Logo",
      logo: "/images/005.jpg",
      fallbackSize: "160x160",
    },
    {
      name: "Logo",
      logo: "/images/006.jpg",
      fallbackSize: "160x160",
    },
    {
      name: "Logo",
      logo: "/images/007.jpg",
      fallbackSize: "160x160",
    },
    {
      name: "Logo",
      logo: "/images/008.jpg",
      fallbackSize: "160x160",
    },
    {
      name: "Logo",
      logo: "/images/009.jpg",
      fallbackSize: "160x160",
    },
    {
      name: "Logo",
      logo: "/images/010.jpg",
      fallbackSize: "160x160",
    },
  ];

  return (
    <section
      className="py-16 md:py-20 px-4 transition-colors duration-300"
      style={{ backgroundColor: colors.background, color: colors.foreground }}
    >
      <div className="max-w-6xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-3xl md:text-4xl font-bold text-center mb-12 md:mb-16"
        >
          <span
            className="bg-clip-text text-transparent"
            style={{
              background: colors.gradient,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Trusted by Industry Leaders
          </span>
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-5 gap-8 md:gap-12 items-center justify-items-center"
        >
          {companies.map((company, index) => (
            <motion.div
              key={company.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }} // Reduced delay timing
              whileHover={{
                scale: 1.05, // Reduced from 1.08
                y: -6, // Reduced from -12
                transition: { duration: 0.2 }, // Faster transition
              }}
              // Removed the continuous scale animation that was causing flickering
              className="group relative w-32 h-32 md:w-40 md:h-40 rounded-xl flex items-center justify-center 
                       backdrop-blur-md border transition-all duration-300 cursor-pointer
                       shadow-xl overflow-hidden"
              style={{
                backgroundColor: `${colors.card}95`,
                borderColor: `${colors.primary}40`,
                boxShadow: `0 8px 32px ${colors.primary}20, 0 4px 16px ${colors.primary}15, inset 0 1px 0 ${colors.primary}20`,
                backdropFilter: "blur(16px) saturate(1.8)",
              }}
            >
              {/* Simplified glow effect - removed transform and reduced blur */}
              <div
                className="absolute inset-0 rounded-xl opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary}20, ${colors.secondary}15)`, // Reduced opacity
                  filter: "blur(8px)", // Reduced from 12px
                  boxShadow: `0 0 30px ${colors.primary}25`, // Reduced glow
                }}
              />

              {/* Simplified inner acrylic layer */}
              <div
                className="absolute inset-1 rounded-lg opacity-50" // Reduced opacity
                style={{
                  background: `linear-gradient(135deg, ${colors.background}25, ${colors.card}15)`, // Reduced opacity
                  backdropFilter: "blur(6px)", // Reduced blur
                }}
              />

              {/* Logo container - NO PADDING */}
              <div className="relative z-10 flex items-center justify-center w-full h-full">
                <img
                  src={company.logo}
                  alt={`${company.name} logo`}
                  className="w-full h-full object-contain filter transition-all duration-300
                           group-hover:brightness-110 group-hover:contrast-105 group-hover:saturate-105" // Reduced filter effects
                  style={{
                    filter: `contrast(1.05) brightness(0.98) saturate(1.05)`, // Reduced filter intensity
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    const fallback = target.nextElementSibling as HTMLElement;
                    target.style.display = "none";
                    if (fallback) {
                      fallback.style.display = "flex";
                    }
                  }}
                />

                {/* Enhanced fallback logo */}
                <div
                  className="absolute inset-0 items-center justify-center hidden"
                  style={{ display: "none" }}
                >
                  <div
                    className="text-center p-3 rounded-lg backdrop-blur-md border"
                    style={{
                      backgroundColor: `${colors.card}90`,
                      border: `1px solid ${colors.primary}40`,
                      boxShadow: `0 4px 16px ${colors.primary}20`,
                    }}
                  >
                    <div
                      className="font-bold text-sm md:text-base mb-1"
                      style={{ color: colors.primary }}
                    >
                      {company.name}
                    </div>
                    <div
                      className="text-xs opacity-70"
                      style={{ color: colors.foreground }}
                    >
                      {company.fallbackSize}
                    </div>
                  </div>
                </div>
              </div>

              {/* Simplified company name tooltip */}
              <div
                className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 
                         opacity-0 group-hover:opacity-100 transition-opacity duration-200" // Faster transition
                style={{
                  backgroundColor: `${colors.primary}90`, // Reduced opacity
                  color: colors.background,
                  boxShadow: `0 2px 8px ${colors.primary}30`, // Reduced shadow
                  fontSize: "0.75rem",
                  fontWeight: "500",
                  padding: "0.5rem 0.75rem",
                  borderRadius: "0.5rem",
                  whiteSpace: "nowrap",
                  backdropFilter: "blur(8px)",
                }}
              >
                {company.name}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
